import './MapStyleControl.scss'

import { showPremiumModal } from '@bikemap/js/integrations/premium'
import { gettext, user } from '@bikemap/js/services'
import { renderIcon, renderSVG, renderCloseButton, renderImg, removeAllChildren } from '@bikemap/js/utility'

/**
 * Map control with a dropdown to switch between different map styles. Implements the maplibregl IControl interface.
 * @see https://docs.mapbox.com/mapbox-gl-js/api/?size=n_10_n#icontrol
 */
export default class MapStyleControl {

    /**
     * @callback selectHandler
     * @param {Object} style
     */

    /** @type {Array|undefined} */
    _mapStyles

    /** @type {Object|undefined} */
    _initialMapStyle

    /** @type {selectHandler} */
    _onSelect

    /** @type {Object} Map */
    _map

    /** @type {HTMLElement} */
    _container

    /** @type {HTMLElement} */
    _mapStylesWrapper

    /** @type {HTMLElement} */
    _activeBtn

    /** @type {Object} */
    _buttons = {}

    /** @type {HTMLButtonElement} */
    _moreBtn

    /** @type {boolean} */
    _keepExpanded = false

    /** Timeout ID */
    _expandTimeout

    /**
     * @param {Object} options Options for the map style control
     * @param {Array} [options.mapStyles] Array of the selectable map styles
     * @param {Object} [options.initialMapStyle] The map style selected by default
     * @param {selectHandler} options.onSelect Called when a map style is selected by the user
     */
    constructor(options) {
        this._mapStyles = options.mapStyles
        this._initialMapStyle = options.initialMapStyle
        this._onSelect = options.onSelect
    }

    /** @inheritdoc */
    onAdd(map) {
        this._map = map
        this._container = document.createElement('div')
        this._container.className = 'maplibregl-ctrl maplibregl-ctrl-group bikemap-ctrl map-style-ctrl'

        // Label for the whole control
        const label = document.createElement('div')
        label.className = 'ctrl-label'
        label.textContent = gettext('Map style')
        this._container.append(label)

        // Close button, shown when expanded
        const closeBtn = renderCloseButton()
        closeBtn.addEventListener('click', e => {
            this.close()
        })
        this._container.append(closeBtn)

        // Wrapper with all map style buttons
        this._mapStylesWrapper = document.createElement('div')
        this._mapStylesWrapper.className = 'map-styles-wrapper'
        this._renderStyleButtons(this._mapStylesWrapper)
        this._container.append(this._mapStylesWrapper)

        // Button to expand and see all available styles
        this._moreBtn = document.createElement('button')
        this._moreBtn.className = 'more-btn' +
            (!this._mapStyles || this._mapStyles.length < 4 ? ' hidden' : '')
        this._moreBtn.title = gettext('Show more')

        // Icon for large screens
        const expandIcon = renderIcon('i-expand', 'primary')
        this._moreBtn.append(expandIcon)

        // Button to toggle the list on xs
        const toggleBtn = document.createElement('button')
        toggleBtn.className = 'toggle-btn'
        toggleBtn.title = gettext('Map style')

        // Icon for small screens
        const layersIcon = renderIcon('i-layers', 'primary')
        toggleBtn.append(layersIcon)

        // Expand handlers for the button
        const clickHandler = e => {
            // Active interaction, so the ctrl will stay expanded
            this.expand(true)
        }
        this._moreBtn.addEventListener('click', clickHandler)
        toggleBtn.addEventListener('click', clickHandler)
        this._moreBtn.addEventListener('mouseenter', e => {
            // Passive interaction triggers expansion after a short delay
            this._expandTimeout = setTimeout(() => {
                this.expand()
                this._expandTimeout = null
            }, 300)
        })
        this._moreBtn.addEventListener('mouseleave', e => {
            // Prevent from expanding, because mouse has already left the button
            if (this._expandTimeout) {
                clearTimeout(this._expandTimeout)
                this._expandTimeout = null
            }
        })

        this._container.append(this._moreBtn)
        this._container.append(toggleBtn)

        // Passively close on mouse leave
        this._container.addEventListener('mouseleave', e => {
            this.close(true)
        })

        return this._container
    }

    /**
     * Render map style buttons inside a wrapper.
     * @param {HTMLElement} wrapper
     */
    _renderStyleButtons(wrapper) {
        if (this._mapStyles) {
            for (const style of this._mapStyles) {
                wrapper.append(this._renderStyleButton(style))
            }
        } else {
            this._renderLoadingPlaceholders(wrapper)
        }
    }

    /**
     * Create a button for a map style.
     * @param {Object} style Map style
     */
    _renderStyleButton(style) {
        const btn = document.createElement('button')
        btn.className = 'map-style-btn'
        if (style === this._initialMapStyle) {
            btn.classList.add('active')
            this._activeBtn = btn
        }
        if (style.dark) {
            btn.classList.add('dark')
        }

        // Map style preview image
        btn.append(renderImg(style.thumbnail, style.name))

        // Label for the map style
        const label = document.createElement('div')
        label.className = 'map-style-label'
        label.textContent = style.name
        btn.append(label)

        // Premium badge if premium style
        if (style.isPremium) {
            btn.classList.add('premium')
            const badge = renderSVG('premium-badge')
            badge.classList.add('premium-badge')
            btn.append(badge)
        }

        // Select handler
        btn.addEventListener('click', async () => {
            if (!btn.classList.contains('active')) {
                if (style.isPremium && !(await user.getIsPremium())) {
                    showPremiumModal()
                } else {
                    if (this._onSelect) this._onSelect(style)
                }
            }
            this.close()
        })

        this._buttons[style.name] = btn
        return btn
    }

    /**
     * Render loading placeholders for the map style buttons inside a wrapper.
     * @param {HTMLElement} wrapper
     */
    _renderLoadingPlaceholders(wrapper) {
        for (let i = 0; i < 3; i++) {
            const placeholder = document.createElement('div')
            placeholder.className = 'map-style-btn-placeholder'
            wrapper.append(placeholder)
        }
    }

    /**
     * Expand to show all buttons.
     * @param {boolean} [keepExpanded] Ctrl will stay open until closed actively
     */
    expand(keepExpanded = false) {
        this._container.classList.add('expanded')
        if (keepExpanded) {
            this._keepExpanded = true
        }
    }

    /**
     * Close expanded ctrl.
     * @param {boolean} [passive] Triggered by a passive user interaction
     */
    close(passive = false) {
        if (!passive || !this._keepExpanded) {
            this._mapStylesWrapper.scrollTop = 0
            this._container.classList.remove('expanded')
            this._keepExpanded = false
        }
    }

    /** @inheritdoc */
    onRemove() {
        this._container.parentNode.removeChild(this._container)
        this._map = undefined
    }

    /**
     * The current map style.
     * @param {Object} style
     */
    setMapStyle(style) {
        if (this._activeBtn) this._activeBtn.classList.remove('active')
        this._activeBtn = this._buttons[style.name]
        if (this._activeBtn) this._activeBtn.classList.add('active')
    }

    /**
     * Set the list of available map styles.
     * @param {import('@bikemap/js/api/services/MapStyleApiService').MapStyle[]} mapStyles
     */
    setMapStyles(mapStyles) {
        this._mapStyles = mapStyles
        this._initialMapStyle = mapStyles[0]

        if (this._mapStylesWrapper) {
            removeAllChildren(this._mapStylesWrapper)
            this._renderStyleButtons(this._mapStylesWrapper)
        }

        if (this._moreBtn) {
            this._moreBtn.classList.toggle('hidden', mapStyles.length < 4)
        }
    }

}
